import { Link, useParams } from 'react-router-dom'
import { Badge } from '@/components/ui'
import { cn } from '@/lib/utils'
import type { LinkedEntityType } from '@/types'
import { getLinkedEntityLabel, getLinkedEntityPath, getLinkedEntityShortId, resolveLinkedEntityType } from './linkedEntity'

interface Props {
  type: LinkedEntityType | null
  number: number | null
  fallbackId?: string | null
  entityId?: string | null
  available?: boolean | null
  status?: string | null
}

// Background color of the right-hand ID segment, by entity type.
const idSegmentColor: Partial<Record<LinkedEntityType, string>> = {
  WorkItem: 'bg-info-foreground ',
  Intent: 'bg-purple-foreground',
}

export function LinkedEntityBadge({ type, number, fallbackId, entityId, available, status }: Props) {
  const { projectId } = useParams()
  const currentType = resolveLinkedEntityType(type, status)
  const shortId = getLinkedEntityShortId(currentType, number)
  const label = getLinkedEntityLabel(currentType)

  if (label && entityId && available === false) {
    return (
      <Badge
        variant="outline"
        className="text-xs text-muted-foreground"
        title={`This ${label.toLowerCase()} was deleted or is no longer available`}
        data-testid="linked-entity-unavailable"
      >
        {label} unavailable
      </Badge>
    )
  }

  const isProjectScope = currentType === 'Project'

  if (shortId || isProjectScope) {
    const idColor = (currentType && idSegmentColor[currentType]) || 'bg-slate-500'
    const badge = (
      <span className="inline-flex shrink-0 items-stretch overflow-hidden rounded-md border text-xs font-medium">
        {label && (
          <span className="flex items-center bg-background px-2 py-0.5 text-foreground">{label}</span>
        )}
        {shortId && (
          <span className={cn('flex items-center px-2 py-0.5 text-background', idColor)}>{shortId}</span>
        )}
      </span>
    )

    const path = projectId && entityId ? getLinkedEntityPath(projectId, currentType, entityId) : null
    if (!path) return badge

    return (
      <Link
        to={path}
        onClick={(e) => e.stopPropagation()}
        aria-label={shortId ? `Open ${label ?? 'linked item'} ${shortId}` : `Open ${label ?? 'linked item'}`}
        className="inline-flex shrink-0 rounded-md hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid="linked-entity-link"
      >
        {badge}
      </Link>
    )
  }

  if (fallbackId) {
    return <Badge variant="outline" className="text-xs">{fallbackId}</Badge>
  }

  return null
}
